/**
 * ticketflow/CentralConsole — coquille de la CONSOLE CENTRALE (ticket-admin.amia.fr).
 * @author Dr Hamid MADANI <drmdh@msn.com>
 *
 * Couche hôte (spécifique TicketFlow, hors modules neutres). Flux :
 *   1. login MANAGER au central (cookie HttpOnly de session) ;
 *   2. accueil : liste des sites (présence) + PROVISIONING d'un site (saisie
 *      id/nom → le central génère le TOKEN DE CONNEXION à copier dans l'agent) ;
 *   3. sélection d'un site → admin complet relayé (baseUrl = /api/sites/<id>/proxy),
 *      avec son propre login de site (les 16 sections : config, audit, voix…).
 *
 * Les appels /api/console* et /api/sites sont RELATIFS (même origine) → le cookie
 * de session voyage automatiquement, y compris sur les appels relayés de l'admin.
 */
import React, { useCallback, useEffect, useState } from 'react';
import EnrollmentPanel from './EnrollmentPanel';
import ConsoleSauvegarde from './ConsoleSauvegarde';
import { AuthProvider } from '../ui/AuthProvider';
import { TICKETFLOW_API_CONFIG, TICKETFLOW_TOKEN_KEY } from './appConfig';
import App from '../App';

const jget = async (url) => fetch(url, { credentials: 'same-origin' });
const jpost = async (url, body) =>
  fetch(url, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}) });

/** Écran de connexion de la console centrale (manager). */
function ConsoleLogin({ onLogged }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const r = await jpost('/api/console/login', { username: username.trim(), password });
      if (!r.ok) { setError('Identifiants invalides'); return; }
      onLogged((await r.json()).data.user);
    } catch (err) { setError(String(err?.message || err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="centered">
      <form className="card" onSubmit={submit}>
        <h1>Console centrale TicketFlow</h1>
        <h2>Connexion manager</h2>
        <label>Utilisateur</label>
        <input value={username} onChange={(e) => setUsername(e.target.value)} autoFocus />
        <label>Mot de passe</label>
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
        {error && <p className="error">{error}</p>}
        <button type="submit" disabled={busy || !username || !password}>Se connecter</button>
      </form>
    </div>
  );
}

/** Accueil : liste des sites + provisioning (token de connexion). */
function SitesHome({ user, onOpen, onLogout }) {
  const [sites, setSites] = useState(null);
  const [msg, setMsg] = useState('');
  const [form, setForm] = useState({ id: '', name: '' });
  const [created, setCreated] = useState(null); // { id, token } à copier

  const reload = useCallback(async () => {
    try {
      const r = await jget('/api/sites');
      if (r.status === 401) { onLogout(); return; }
      setSites((await r.json()).data || []);
    } catch (e) { setMsg(String(e?.message || e)); }
  }, [onLogout]);
  useEffect(() => { reload(); const t = setInterval(reload, 5000); return () => clearInterval(t); }, [reload]);

  const addSite = async (e) => {
    e.preventDefault(); setMsg(''); setCreated(null);
    const r = await jpost('/api/sites', form);
    const d = await r.json();
    if (!r.ok) { setMsg(d.error || 'Échec'); return; }
    setCreated(d.data); setForm({ id: '', name: '' }); reload();
  };
  const del = async (id) => {
    if (!window.confirm(`Retirer le site ${id} ?`)) return;
    await fetch(`/api/sites/${id}`, { method: 'DELETE', credentials: 'same-origin' });
    reload();
  };
  const logout = async () => { await jpost('/api/console/logout'); onLogout(); };

  return (
    <div className="central-home">
      <header className="central-topbar">
        <h1>Console centrale — sites</h1>
        <div><span className="muted">{user?.username} (manager)</span> <button type="button" className="ghost" onClick={logout}>Déconnexion</button></div>
      </header>

      {msg && <div className="status">{msg}</div>}

      <div className="card">
        <h2>Sites gérés</h2>
        {sites === null ? <p className="muted">…</p> : sites.length === 0 ? (
          <p className="muted">Aucun site. Ajoutez-en un ci-dessous.</p>
        ) : (
          <table className="perm-matrix">
            <thead><tr><th>Site</th><th>État</th><th>Vu il y a</th><th /></tr></thead>
            <tbody>
              {sites.map((s) => (
                <tr key={s.id}>
                  <td><b>{s.name}</b> <span className="muted">({s.id})</span></td>
                  <td>{s.online ? <span className="badge">en ligne</span> : <span className="muted">hors ligne</span>}</td>
                  <td>{s.online ? `${s.lastSeenSec}s` : '—'}</td>
                  <td>
                    <button type="button" onClick={() => onOpen(s)} disabled={!s.online} title={s.online ? '' : 'Site hors ligne'}>Gérer</button>
                    <button type="button" className="ghost danger" onClick={() => del(s.id)} title="Retirer">✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* L'enrôlement : le client saisit un code, la machine se présente, un clic suffit.
          C'est le chemin NORMAL depuis la 0.2.0. Le formulaire manuel ci-dessous est conservé
          pour les sites déjà en production et les cas hors-ligne — on ne casse pas l'existant. */}
      <EnrollmentPanel />

      {/* La sauvegarde de la CONSOLE vit ici, au niveau des sites — et non dans l'admin d'un
          site. Ce n'est pas la configuration d'un client qu'on sauvegarde, c'est l'identité
          de l'éditeur : sa clé de signature, ses sites, son enrôlement, son magasin. Repliée
          par défaut : on ne la manipule qu'aux moments qui comptent, et un bouton de
          restauration en évidence serait une invitation à l'accident. */}
      <details className="card" style={{ padding: '10px 14px' }}>
        <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
          💾 Sauvegarde et migration de la console
          <span className="muted" style={{ fontWeight: 400 }}> — clé de signature, sites, enrôlement, magasin</span>
        </summary>
        <div style={{ marginTop: 14 }}><ConsoleSauvegarde /></div>
      </details>

      <div className="card">
        <h2>Ajouter un site à la main (ancien chemin)</h2>
        <p className="muted">
          Réservé aux cas particuliers : site déjà installé, ou client sans accès à Internet au
          moment de la pose. Le chemin recommandé est l’enrôlement par code, ci-dessus.
        </p>
        <form className="server-add" onSubmit={addSite}>
          <input placeholder="identifiant (ex. cabinet-alger)" value={form.id} onChange={(e) => setForm({ ...form, id: e.target.value })} />
          <input placeholder="nom (ex. Cabinet Alger)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <button type="submit" disabled={!form.id.trim()}>Générer le token</button>
        </form>
        {created && (
          <div className="token-created">
            <p>Token de connexion pour <b>{created.id}</b> — à installer dans l'agent du serveur de site (variable <code>SITE_TOKEN</code>) :</p>
            <code className="token-value">{created.token}</code>
            <p className="muted">Notez-le : il n'est affiché qu'une fois.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function CentralConsole() {
  const [user, setUser] = useState(undefined); // undefined=chargement, null=déconnecté
  const [site, setSite] = useState(null);

  useEffect(() => {
    (async () => {
      try { const r = await jget('/api/console/me'); setUser(r.ok ? (await r.json()).data.user : null); }
      catch { setUser(null); }
    })();
  }, []);

  if (user === undefined) return <div className="centered"><p className="muted">…</p></div>;
  if (!user) return <ConsoleLogin onLogged={setUser} />;

  // Un site est sélectionné → admin complet relayé (login de site + 16 sections).
  if (site) {
    const apiConfig = { ...TICKETFLOW_API_CONFIG, baseUrl: `/api/sites/${site.id}/proxy` };
    return (
      <div className="central-managed">
        <div className="central-managed-bar">
          <button type="button" className="ghost" onClick={() => setSite(null)}>← Sites</button>
          <span className="muted">Site géré : <b>{site.name}</b></span>
        </div>
        <AuthProvider apiConfig={apiConfig} tokenKey={`${TICKETFLOW_TOKEN_KEY}-${site.id}`}>
          <App centralMode siteLabel={site.name} siteId={site.id} />
        </AuthProvider>
      </div>
    );
  }

  return <SitesHome user={user} onOpen={setSite} onLogout={() => { setUser(null); setSite(null); }} />;
}
